import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';

const GLYPHS = '01<>{}[]/$#@*&ABCDEF:;=+';

/**
 * Soft falling glyphs. Canvas based, pauses off screen / hidden tab.
 * Kept low opacity so it feels like atmosphere, not a Matrix screensaver.
 */
export default function CodeRain({ className = '', density = 18, opacity = 0.28 }) {
  const canvasRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || prefersReducedMotion) return undefined;

    const context = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    let columns = [];
    let frame;
    let visible = true;
    let tick = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.max(8, Math.min(density, Math.floor(width / 42)));
      columns = Array.from({ length: count }, (_, index) => ({
        x: ((index + 0.5) / count) * width,
        y: Math.random() * height,
        speed: 0.35 + Math.random() * 0.55,
        chars: Array.from({ length: 10 + Math.floor(Math.random() * 8) }, () =>
          GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
        )
      }));
    };

    const draw = () => {
      tick += 1;
      context.clearRect(0, 0, width, height);
      context.font = '11px "JetBrains Mono", ui-monospace, monospace';

      const isDark = document.documentElement.classList.contains('dark');
      const color = isDark ? '52, 211, 153' : '37, 99, 235';

      columns.forEach((column) => {
        column.y += column.speed;
        if (column.y > height + column.chars.length * 14) {
          column.y = -column.chars.length * 14;
          column.speed = 0.35 + Math.random() * 0.55;
        }

        if (tick % 18 === 0) {
          column.chars[Math.floor(Math.random() * column.chars.length)] =
            GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        }

        column.chars.forEach((char, index) => {
          const alpha = (1 - index / column.chars.length) * 0.55;
          context.fillStyle = `rgba(${color},${alpha})`;
          context.fillText(char, column.x, column.y - index * 14);
        });
      });

      frame = requestAnimationFrame(draw);
    };

    const start = () => {
      if (frame) return;
      frame = requestAnimationFrame(draw);
    };

    const stop = () => {
      if (!frame) return;
      cancelAnimationFrame(frame);
      frame = undefined;
    };

    resize();

    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible && !document.hidden) start();
        else stop();
      },
      { threshold: 0 }
    );
    observer.observe(canvas);

    const onVisibility = () => {
      if (document.hidden || !visible) stop();
      else start();
    };

    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVisibility);
    start();

    return () => {
      stop();
      observer.disconnect();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [density, prefersReducedMotion]);

  if (prefersReducedMotion) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ opacity }}
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    />
  );
}
